import { useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useQRCodeScanner, classifyQRCode, type QRScanResult } from '@/hooks/useQRCodeScanner';
import { useToast } from '@/hooks/useToast';
import { ScanLine, Clipboard, X } from 'lucide-react';

interface QRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult: (result: QRScanResult) => void;
}

export function QRScannerModal({
  isOpen,
  onClose,
  onResult,
}: QRScannerModalProps) {
  const { toast } = useToast();
  const { isScanning, error, startScanning, stopScanning } = useQRCodeScanner();

  // Handle scan result
  const handleScanResult = useCallback((result: QRScanResult) => {
    onResult(result);
    stopScanning();
    onClose();
  }, [onResult, stopScanning, onClose]);

  // Handle paste from clipboard
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        toast({ 
          variant: 'destructive', 
          title: 'Clipboard empty', 
          description: 'No text found in clipboard' 
        });
        return;
      }

      const result = classifyQRCode(text.trim());
      handleScanResult(result);
    } catch (err) {
      console.error('Error pasting from clipboard:', err);
      toast({ 
        variant: 'destructive', 
        title: 'Paste failed', 
        description: 'Could not read from clipboard' 
      });
    }
  };

  // Start/stop scanning based on isOpen
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the DOM element is ready
      const timer = setTimeout(() => {
        startScanning(handleScanResult);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      stopScanning();
    }
  }, [isOpen, startScanning, stopScanning, handleScanResult]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        stopScanning();
        onClose();
      }
    }}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[min(90vw,500px)] p-3 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ScanLine className="h-5 w-5" />
            Scan QR Code
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Position the QR code within the camera view to scan
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full" style={{ aspectRatio: '1 / 1', maxHeight: '50vh' }}>
          {/* Scanner requires element with id="qr-reader" */}
          <div
            id="qr-reader"
            className="w-full h-full rounded-xl overflow-hidden bg-muted"
          />

          {/* Error overlay */}
          {error && (
            <div className="absolute inset-x-2 sm:inset-x-4 top-2 sm:top-4 p-2 sm:p-3 bg-destructive text-destructive-foreground rounded-lg text-center z-50 shadow-lg text-xs sm:text-sm">
              {error}
            </div>
          )}

          {/* Scanning indicator */}
          {isScanning && !error && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-32 h-32 sm:w-48 sm:h-48 border-2 border-primary rounded-lg opacity-50" />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center mt-2 sm:mt-4">
          <Button
            onClick={handlePasteFromClipboard}
            variant="outline"
            className="gap-2 text-sm"
            size="sm"
          >
            <Clipboard className="h-4 w-4" />
            Paste from Clipboard
          </Button>
          <Button
            onClick={() => {
              stopScanning();
              onClose();
            }}
            variant="ghost"
            size="sm"
            className="text-sm"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>

        <p className="text-[10px] sm:text-xs text-muted-foreground text-center mt-1 sm:mt-2">
          Scan a Cashu token, Lightning invoice, or Lightning address
        </p>
      </DialogContent>
    </Dialog>
  );
}
